// English (US) UI strings. id.ts must provide every key (enforced by its type).
export const en = {
  // common
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'flow.back_to_builder': 'Back to the builder',
  'common.reload': 'Reload',
  'common.save': 'Save',
  'common.collapse': 'Collapse',
  'common.expand': 'Expand',
  'common.add': 'Add',
  'common.copy': 'Copy',
  'common.open': 'Open',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.loading': 'Loading',
  'common.untitled_questionnaire': 'Untitled questionnaire',
  'common.untitled_question': 'Untitled question',
  'common.anonymous': 'Anonymous',
  'common.required': 'Required',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.action_failed': 'Action failed.',
  'common.load_failed': 'Could not load.',
  'common.unknown_error': 'Something went wrong.',

  'lang.label': 'Language',

  // status
  'status.draft': 'Draft',
  'status.published': 'Live',
  'status.closed': 'Closed',
  'status.archived': 'Archived',

  // roles
  'role.admin': 'Administrator',
  'role.admin.plural': 'Administrators',
  'role.admin.desc': 'Manages users and can access every questionnaire.',
  'role.creator': 'Researcher',
  'role.creator.plural': 'Researchers',
  'role.creator.desc': 'Creates, edits and publishes their own and shared questionnaires.',
  'role.analyst': 'Analyst',
  'role.analyst.plural': 'Analysts',
  'role.analyst.desc': 'Reviews results and exports for questionnaires shared with them.',
  'role.respondent': 'Respondent',
  'role.respondent.plural': 'Respondents',
  'role.respondent.desc': 'Answers surveys on web and in the mobile app.',
  'role.enumerator': 'Enumerator',
  'role.enumerator.plural': 'Enumerators',
  'role.enumerator.desc': 'Collects responses for surveys assigned by an administrator.',
  'users.manage_roles': 'Edit roles and permissions',
  'nav.roles': 'Roles & permissions',
  'roles.title': 'Roles & permissions',
  'roles.sub': 'Each role grants a set of permissions and reaches either every questionnaire or only the ones its users own or are shared with. Changes apply immediately.',
  'roles.assign_link': 'Assign roles to users',
  'roles.load_failed': 'Could not load roles.',
  'roles.new': 'New role',
  'roles.edit': 'Edit',
  'roles.edit_title': 'Edit role: {name}',
  'roles.delete': 'Delete',
  'roles.delete_confirm': 'Delete the role "{name}"?',
  'roles.delete_in_use': 'Move its users to another role first.',
  'roles.deleted': 'Role deleted.',
  'roles.created': 'Role created.',
  'roles.saved': 'Role saved.',
  'roles.save': 'Save role',
  'roles.saving': 'Saving…',
  'roles.key': 'Key',
  'roles.key_hint': 'Permanent identifier: lowercase letters, digits and underscores, e.g. field_lead.',
  'roles.name': 'Name',
  'roles.description': 'Description',
  'roles.locked': 'Fixed',
  'roles.locked_hint': "This role's permissions are fixed because the app depends on them (administrators keep full access; respondents and enumerators work through their own flows). Only the description can change.",
  'roles.built_in': 'Built-in',
  'roles.custom': 'Custom',
  'roles.scope': 'Reach',
  'roles.scope_all': 'Every questionnaire',
  'roles.scope_all_hint': 'The permissions below apply to all questionnaires in the workspace.',
  'roles.scope_all_short': 'All',
  'roles.scope_shared': 'Own and shared questionnaires',
  'roles.scope_shared_hint': 'Questionnaires the user created or that were shared with them as editor or viewer.',
  'roles.scope_shared_short': 'Own & shared',
  'roles.permission': 'Permission',
  'roles.permission_count': 'Permissions: {count}',
  'roles.user_count': 'Users: {count}',
  'roles.scope_own_flow': 'Own flow only (public links or mobile assignments)',
  'roles.matrix': 'Permission matrix',
  'roles.matrix_sub': 'What each role may do. The server checks every request against this.',
  'roles.allowed': 'Allowed',
  'perm.group.surveys': 'Questionnaires',
  'perm.group.fieldwork': 'Fieldwork',
  'perm.group.responses': 'Responses',
  'perm.group.administration': 'Administration',
  'perm.survey.create': 'Create questionnaires',
  'perm.survey.create.hint': 'Start new questionnaires and duplicate existing ones.',
  'perm.survey.view': 'View questionnaires',
  'perm.survey.view.hint': "Open questionnaires within the role's reach.",
  'perm.survey.edit': 'Edit questionnaires',
  'perm.survey.edit.hint': 'Change questions, logic and settings.',
  'perm.survey.publish': 'Publish questionnaires',
  'perm.survey.publish.hint': 'Publish approved versions and close or reopen collection.',
  'perm.survey.delete': 'Delete and archive questionnaires',
  'perm.survey.delete.hint': 'Archive, restore or permanently delete.',
  'perm.survey.share': 'Share questionnaires',
  'perm.survey.share.hint': 'Give other users editor or viewer access.',
  'perm.survey.approve': 'Approve questionnaire versions',
  'perm.survey.approve.hint': 'Approve or reject a revision before it can be published.',
  'perm.survey.enumerators.assign': 'Assign enumerators and respondents',
  'perm.survey.enumerators.assign.hint': 'Choose who collects each questionnaire, map respondents to enumerators and run group randomization.',
  'perm.respondents.manage': 'Manage the respondent roster',
  'perm.respondents.manage.hint': 'Add and correct named respondents, including gender.',
  'perm.responses.view': 'View responses and results',
  'perm.responses.view.hint': 'Dashboards, response lists, cross-tabs.',
  'perm.responses.export': 'Export responses',
  'perm.responses.export.hint': 'Download CSV, Excel and SPSS files.',
  'perm.responses.approve': 'Approve responses',
  'perm.responses.approve.hint': 'Approve or reject collected responses; exports wait for approval.',
  'perm.users.manage': 'Manage users',
  'perm.users.manage.hint': "Create, disable and change the role of users (never above the manager's own permissions).",
  'perm.audit.view': 'Read the audit log',
  'perm.audit.view.hint': 'The workspace-wide history of sign-ins and changes.',
  'perm.settings.manage': 'Manage workspace settings',
  'perm.settings.manage.hint': 'For example, turning the AI assistant on or off.',
  'perm.library.manage_all': "Manage everyone's library questions",
  'perm.library.manage_all.hint': 'Delete question-library items saved by other users.',

  // navigation / layout
  'nav.brand_sub': 'Questionnaire platform',
  'nav.new_questionnaire': 'New questionnaire',
  'nav.workspace': 'Workspace',
  'nav.questionnaires': 'Questionnaires',
  'nav.administration': 'Administration',
  'nav.users_roles': 'Users & roles',
  'nav.audit': 'Activity log',
  'nav.invitations': 'Invitations',
  'nav.settings': 'Settings',
  'a11y.title': 'Accessibility',
  'a11y.summary': '{errors} to fix · {warnings} to consider',
  'a11y.all_good': 'No problems found',
  'a11y.fix': 'Fix',
  'a11y.consider': 'Consider',
  'a11y.open': 'Open',
  'a11y.wcag_title': 'The WCAG 2.2 success criterion this relates to',
  'a11y.hint': "Checked against WCAG 2.2 guidance for forms. Automatic checks can't catch everything: try the questionnaire with a keyboard and a screen reader too.",
  'audit.title': 'Activity log',
  'audit.retention': 'Sign-ins, publishing, approvals, exports, response edits and changes to users and sharing',
  'audit.survey_title': 'Activity',
  'audit.survey_sub': 'Who changed, published, approved or exported this questionnaire',
  'audit.all_actions': 'All activity',
  'audit.filter_action': 'Type of activity',
  'audit.filter_actor': 'Search by person',
  'audit.empty': 'No activity yet',
  'audit.when': 'When',
  'audit.who': 'Who',
  'audit.what': 'What',
  'audit.questionnaire': 'Questionnaire',
  'audit.details': 'Details',
  'audit.more': 'Show {count} more',
  'audit.group.auth': 'Sign-ins',
  'audit.group.survey': 'Questionnaires',
  'audit.group.response': 'Responses',
  'audit.group.user': 'Users',
  'audit.group.library': 'Question library',
  'audit.group.respondent': 'Respondent roster',
  'audit.group.account': 'Own account',
  'audit.auth.login': 'Signed in',
  'audit.auth.login_failed': 'Failed sign-in',
  'audit.auth.register': 'Created an account',
  'audit.account.update': 'Updated their profile',
  'audit.account.password_change': 'Changed their password',
  'audit.user.create': 'Created a user',
  'audit.user.update': 'Changed a user',
  'audit.settings.update': 'Changed workspace settings',
  'audit.survey.create': 'Created a questionnaire',
  'audit.survey.publish': 'Published',
  'audit.survey.approval': 'Reviewed for publishing',
  'audit.survey.closed': 'Closed responses',
  'audit.survey.slug': 'Changed the custom link',
  'audit.survey.auto_close': 'Closed automatically',
  'audit.survey.collection': 'Changed schedule & limits',
  'audit.survey.invites_add': 'Added respondent invitations',
  'audit.survey.invites_send': 'Sent invitations',
  'audit.survey.invites_remind': 'Sent reminders',
  'audit.survey.invites_remove': 'Removed invitations',
  'audit.survey.archived': 'Archived',
  'audit.survey.restore': 'Restored',
  'audit.survey.duplicate': 'Duplicated',
  'audit.survey.delete': 'Deleted a questionnaire',
  'audit.survey.share': 'Shared',
  'audit.survey.unshare': 'Stopped sharing',
  'audit.survey.enumerators': 'Assigned enumerators',
  'audit.survey.respondent_assignments': 'Assigned respondents',
  'audit.survey.group_randomize': 'Randomized groups',
  'audit.survey.weighting': 'Changed weighting',
  'audit.response.approval': 'Reviewed a response',
  'audit.response.edit': 'Edited a response',
  'audit.response.sync': 'Uploaded a response from the mobile app',
  'audit.response.sync_rejected': 'Mobile upload rejected',
  'audit.responses.export': 'Exported responses',
  'audit.responses.test_create': 'Generated test responses',
  'audit.responses.test_delete': 'Deleted test responses',
  'audit.library.save': 'Saved to the question library',
  'audit.library.delete': 'Removed from the question library',
  'audit.respondent.create': 'Added a respondent',
  'audit.respondent.update': 'Changed a respondent',
  'nav.respondents': 'Respondent roster',
  'nav.account_settings': 'Account settings',
  'nav.light_mode': 'Light mode',
  'nav.dark_mode': 'Dark mode',
  'nav.switch_light': 'Switch to light mode',
  'nav.switch_dark': 'Switch to dark mode',
  'nav.sign_out': 'Sign out',
  'nav.open_navigation': 'Open navigation',
  'nav.collapse_sidebar': 'Collapse sidebar to icons',
  'nav.expand_sidebar': 'Expand sidebar',
  'nav.breadcrumb': 'Breadcrumb',
  'nav.main': 'Main',

  // auth
  'auth.welcome_back': 'Welcome back',
  'auth.signin_sub': 'Sign in to manage questionnaires and responses.',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.name': 'Name',
  'auth.sign_in': 'Sign in',
  'auth.signing_in': 'Signing in…',
  'auth.no_account': 'No account?',
  'auth.create_one': 'Create one',
  'auth.signin_failed': 'Sign in failed.',
  'auth.create_title': 'Create an account',
  'auth.create_sub': 'Respond to surveys that require sign-in. An administrator can grant more access later.',
  'auth.password_hint': 'At least 8 characters.',
  'auth.creating': 'Creating…',
  'auth.create_account': 'Create account',
  'auth.already_registered': 'Already registered?',
  'auth.register_failed': 'Registration failed.',
  'auth.invite_sub': 'You’ve been invited to join as {role}. An administrator approves each sign-up before you can sign in.',
  'auth.invite_checking': 'Checking your invitation…',
  'auth.invite_invalid': 'This invitation link is invalid, revoked or already used up. Ask the person who sent it for a new one.',
  'auth.signup_sent_title': 'Sign-up sent',
  'auth.signup_sent_body': 'Thanks! An administrator will review your sign-up. You can sign in once it’s approved.',
  'auth.back_to_signin': 'Back to sign in',
  'auth.show_password': 'Show password',
  'auth.hide_password': 'Hide password',
  'auth.remember_me': 'Remember me',
  'auth.remember_hint': 'Stay signed in on this device. Leave unticked on a shared computer.',

  // errors (client side)
  'error.network': 'Can’t reach the server. Check your connection and try again.',
  'error.request_failed': 'Request failed ({status})',

  // time & duration
  'time.just_now': 'just now',
  'time.min_ago': '{n} min ago',
  'time.hours_ago': '{n} h ago',
  'time.days_ago': '{n} d ago',
  'dur.seconds': '{s}s',
  'dur.min_sec': '{m}m {s}s',
  'dur.min': '{m}m',
  'dur.hour_min': '{h}h {m}m',

  // questionnaire list
  'surveys.title': 'Questionnaires',
  'surveys.new': 'New',
  'surveys.kpi_total': 'Questionnaires',
  'surveys.kpi_live': 'Live now',
  'surveys.kpi_drafts': 'Drafts',
  'surveys.kpi_responses': 'Responses',
  'surveys.filter_all': 'All active',
  'surveys.filter_drafts': 'Drafts',
  'surveys.filter_live': 'Live',
  'surveys.filter_closed': 'Closed',
  'surveys.filter_archived': 'Archived',
  'surveys.search_placeholder': 'Search title, number or code',
  'surveys.search_label': 'Search questionnaires',
  'surveys.load_failed': 'Could not load questionnaires.',
  'surveys.no_matches': 'No matches',
  'surveys.none_yet': 'No questionnaires yet',
  'surveys.create_first': 'Create your first questionnaire',
  'surveys.try_other_filter': 'Try a different filter or search.',
  'surveys.col_no': 'No.',
  'surveys.col_questionnaire': 'Questionnaire',
  'surveys.col_code': 'Code',
  'surveys.col_status': 'Status',
  'surveys.col_owner': 'Owner',
  'surveys.col_responses': 'Responses',
  'surveys.col_updated': 'Updated',
  'surveys.col_actions': 'Actions',
  'surveys.toggle_summary': 'Show or hide summary',
  'surveys.group_by_status': 'Group by status',
  'surveys.toggle_group': 'Show or hide {status}',
  'surveys.shared_with_you': 'Shared with you · {relation}',
  'surveys.actions_for': 'Actions for {title}',
  'surveys.edit': 'Edit',
  'surveys.responses': 'Responses',
  'surveys.duplicate': 'Duplicate',
  'surveys.archive': 'Archive',
  'surveys.restore': 'Restore',
  'surveys.delete': 'Delete',
  'surveys.duplicated': 'Questionnaire duplicated',
  'surveys.archived': 'Archived',
  'surveys.restored': 'Restored',
  'surveys.deleted': 'Deleted',
  'surveys.confirm_delete': 'Delete “{title}”? This cannot be undone.',
  'surveys.confirm_delete_with_responses': 'Delete “{title}” and its {count} responses? This cannot be undone.',
  'relation.owner': 'owner',
  'relation.editor': 'editor',
  'relation.viewer': 'viewer',
  'relation.admin': 'admin',

  // new questionnaire
  'new.title': 'New questionnaire',
  'new.title_label': 'Title',
  'new.title_hint': 'You can change this later.',
  'new.title_placeholder': 'e.g. Customer satisfaction Q4',
  'new.start_from': 'Start from',
  'new.blank': 'Blank',
  'new.blank_text': 'An empty questionnaire.',
  'new.starter': 'Starter',
  'new.starter_text': 'Four questions with a condition and randomized options.',
  'new.create': 'Create and open builder',
  'new.failed': 'Could not create the questionnaire.',
  'starter.welcome': 'Thanks for taking a few minutes to help us improve.',
  'starter.thanks': 'Your response has been recorded. Thank you!',
  'starter.q1': 'How satisfied are you with our service?',
  'starter.q1_min': 'Very unsatisfied',
  'starter.q1_max': 'Very satisfied',
  'starter.q2': 'What could we do better?',
  'starter.q3': 'How did you hear about us?',
  'starter.q3_o1': 'Search engine',
  'starter.q3_o2': 'Friend or colleague',
  'starter.q3_o3': 'Social media',
  'starter.q3_o4': 'Other',
  'starter.q4': 'Would you recommend us to a friend?',

  // survey header tabs
  'tabs.build': 'Build',
  'tabs.settings': 'Settings',
  'tabs.preview': 'Preview',
  'tabs.share': 'Publish & share',
  'tabs.responses': 'Responses',
  'header.code_title': 'Unique questionnaire code',

  // builder
  'builder.load_failed': 'Could not load the questionnaire.',
  'builder.undo': 'Undo',
  'builder.redo': 'Redo',
  'builder.title_label': 'Questionnaire title',
  'builder.publish': 'Publish',
  'builder.publish_changes': 'Publish changes',
  'builder.published': 'Published',
  'builder.reopen': 'Reopen',
  'builder.issues_before_publish': '{count} issues to fix before publishing',
  'builder.issues_before_publish_one': '1 issue to fix before publishing',
  'builder.conflict': 'This questionnaire was changed in another tab or by a teammate. Your latest edits were not saved.',
  'builder.load_latest': 'Load latest version',
  'builder.questions': 'Questions',
  'builder.collapse_panel': 'Collapse panel to icons',
  'builder.expand_panel': 'Expand panel',
  'builder.resize_panel': 'Drag to resize the panel — double-click resets',
  'builder.total': '{count} total',
  'builder.has_logic': 'Has display logic',
  'builder.needs_attention': 'Needs attention',
  'builder.no_questions': 'No questions yet.',
  'builder.add_first': 'Add your first question',
  'builder.add_first_hint': 'Pick a question type to start building.',
  'builder.add_question': 'Add question',
  'builder.add_a_question': 'Add a question',
  'builder.confirm_delete': 'Delete this question?',
  'builder.confirm_delete_deps': 'Delete this question? {count} other questions use it in their display logic; those conditions will be removed.',
  'builder.confirm_delete_deps_one': 'Delete this question? 1 other question uses it in its display logic; that condition will be removed.',
  'builder.published_version': 'Published version {version}',
  'builder.responses_closed': 'Responses closed',
  'builder.settings': 'Settings',
  'builder.shuffle': 'Shuffle',
  'builder.shuffle.off': 'Off — keep written order',
  'builder.shuffle.questions': 'Questions within each section',
  'builder.shuffle.sections': 'Section order',
  'builder.shuffle.both': 'Section order and questions within sections',
  'builder.shuffle.hint': 'Each respondent gets their own order. Fine-tune each section below, e.g. keep the first section fixed and shuffle the rest.',
  'builder.section_shuffle': 'Shuffle per section',
  'builder.untitled_section': 'No section',
  'builder.shuffle_questions_here': 'Shuffle questions',
  'builder.can_move': 'Can move',
  'builder.pinned_untitled': 'Questions without a section stay in place.',
  'builder.pinned_logic': 'Stays in place: linked by display logic or a jump.',
  'builder.outline_view': 'Outline shows',
  'builder.outline_titles': 'Titles',
  'builder.outline_codes': 'Codes',
  'builder.outline_no_code': 'No code',
  'builder.outline_preview': 'Preview',
  'builder.preview_mode_hint': 'See every question as respondents do and edit it in place',
  'builder.side_tabs': "Outline and selected question",
  'builder.side_outline': "Outline",
  'builder.side_selected': "Selected question",
  'all.pick_question': 'Click a question to edit its other settings here.',
  'builder.group_sections': 'Group by section',
  'builder.section_count': '{count} questions',
  'builder.section_count_one': '1 question',
  'builder.toggle_section': 'Show or hide section {title}',
  'builder.view_flow': 'View survey flow',
  'builder.flow_title': 'Survey flow',
  'builder.add_section': 'Add section',
  'builder.rename_section': 'Rename section',
  'builder.group_into_section': 'Group existing questions into a section…',
  'builder.question_needs_section': 'Create a section first: every question belongs to a section.',
  'builder.add_to_section': 'Add to section',
  'builder.add_first_section_hint': 'Questions live in sections. Create your first section, then add questions to it.',
  'builder.drag_section': 'Drag to move this section',
  'builder.leads_to': '{count} other question(s) react to this answer',
  'flow.drag_randomizer': 'Drag to move this randomizer',
  'builder.in_randomizer': 'Randomizer: each respondent sees {present} of {total} sections',
  'builder.remove_section': 'Remove section',
  'builder.confirm_remove_section': 'Remove section “{title}”? Its questions stay where they are.',
  'builder.section_name': 'Section name',
  'builder.section_starts_at': 'Starts at question',
  'builder.section_starts_hint': 'This question and the following ones in the same section move into the new section.',
  'builder.sections_total': '{count} sections',
  'builder.sections_total_one': '1 section',
  'builder.outline_search': 'Search questions…',
  'builder.outline_matches': '{count} of {total} questions',
  'builder.outline_no_match': 'No questions match.',
  'builder.clear_filters': 'Clear',
  'builder.view_options': 'View options',
  'builder.sections_label': 'Sections',
  'builder.filter': 'Filter questions',
  'builder.filter.all': 'All',
  'builder.filter.logic': 'Logic',
  'builder.filter.issues': 'Needs attention',
  'builder.filter.required': 'Required',
  'builder.expand_all': 'Expand all sections',
  'builder.collapse_all': 'Collapse all sections',
  'builder.shuffle_on': 'Shuffled',
  'builder.login_required': 'Login required',
  'builder.allow_back': "Respondents can go back",
  'builder.allow_back_on': "A Back button lets respondents return to earlier questions and change their answers.",
  'builder.allow_back_off': "No Back button: once a page is answered it cannot be revised.",
  'flow.ov_moving': "Moving through the survey",
  'builder.show_progress': 'Show progress bar',
  'builder.show_timer': 'Show elapsed timer to respondents',
  'builder.captcha': 'Human check (captcha)',
  'builder.captcha.end': 'Before submitting',
  'builder.captcha.start': 'At the start',
  'builder.captcha.both': 'At the start and before submitting',
  'builder.captcha.hint': "Where respondents see Cloudflare's \"Verify you are human\" box on the web link. Every submission is checked either way; the mobile app is not affected.",
  'builder.page_mode': 'Page layout',
  'builder.page_mode.question': 'One question per page',
  'builder.page_mode.section': 'One section per page',
  'builder.page_mode.all': 'All questions on one page',
  'builder.page_mode.question.hint': 'Respondents see one question at a time.',
  'builder.page_mode.section.hint': 'Consecutive questions with the same section title share a page. Questions without a section title get their own page.',
  'builder.page_mode.all.hint': 'Every question appears on a single scrolling page. Conditional questions appear as soon as they apply.',
  'builder.require_auth': 'Require respondents to sign in',
  'builder.description': 'Description',
  'builder.welcome_message': 'Welcome message',
  'builder.thankyou_message': 'Thank-you message',
  'settings.endings': 'Endings',
  'settings.no_endings': 'No extra endings. Everyone sees the thank-you message.',
  'settings.ending_n': 'Ending {n}',
  'settings.ending_name': 'Name (only you see it)',
  'settings.ending_message': 'Message respondents see',
  'settings.redirect': 'Redirect link',
  'settings.redirect_placeholder': 'Optional: send respondents to an https link afterwards',
  'settings.remove_ending': 'Remove ending',
  'settings.used_by': 'Used by {count} jump rules',
  'settings.used_by_one': 'Used by 1 jump rule',
  'settings.endings_hint': "Point a question's jump rule at an ending to finish there. Answers so far are still recorded.",
  'settings.quotas': 'Quotas',
  'settings.no_quotas': 'No quotas. Every response is accepted.',
  'settings.quota_n': 'Quota {n}',
  'settings.quota_name': 'Quota name',
  'settings.quota_limit': 'Limit',
  'settings.quota_count': '{n} / {limit}',
  'settings.quota_count_title': 'Responses counted so far',
  'settings.quota_counts_when': 'Counts responses that match',
  'settings.quota_message': 'Message for screened-out respondents (optional)',
  'settings.remove_quota': 'Remove quota',
  'settings.quotas_hint': "When a quota is full, web respondents who match it are thanked and stop; their answers aren't recorded. Field interviews from the app are always kept.",
  'settings.hidden_fields': 'Hidden fields',
  'settings.field_name': 'Field name',
  'settings.field_placeholder': 'e.g. region or source',
  'settings.remove_field': 'Remove {name}',
  'settings.hidden_fields_hint': 'Add the field to the link as ?name=value. The value is saved with each response, appears in exports, and can be shown in questions with {{name}}.',
  'settings.loops': 'Loop & merge',
  'settings.loops_need_sections': 'Give questions a section title first; then a section can repeat.',
  'settings.loop_none': "Don't repeat",
  'settings.loop_no_source': 'Add a multiple choice question before this section to loop over',
  'settings.loop_for_each': 'Repeat for each option chosen in {n}. {title}',
  'settings.loops_hint': 'Questions in a repeating section are asked once per chosen option. Use {{loop}} in the question text to show the option.',
  'settings.theme': 'Look',
  'settings.theme_primary': 'Accent color',
  'settings.theme_background': 'Page background',
  'settings.theme_default': 'Default',
  'settings.theme_reset': 'Use the default',
  'settings.theme_font': 'Font',
  'settings.font_system': 'Standard',
  'settings.theme_zoom': 'Display size',
  'settings.theme_zoom_hint': 'Makes the questionnaire bigger or smaller for respondents, for example on tablets or large screens.',
  'settings.zoom_standard': 'Standard (100%)',
  'settings.font_serif': 'Serif',
  'settings.font_rounded': 'Rounded',
  'settings.font_mono': 'Monospace',
  'settings.theme_logo': 'Logo',
  'settings.logo_hint': 'Shown at the top of the questionnaire',
  'settings.logo_set': 'Logo added',
  'settings.theme_preview': 'Preview of the accent and background',
  'settings.languages': 'Languages',
  'settings.base_language': 'Questionnaire written in',
  'settings.base_language_hint': 'Respondents who pick another language see this text wherever a translation is missing.',
  'settings.add_language': 'Add a language',
  'settings.choose_language': 'Choose a language…',
  'settings.edit_translation': 'Translate',
  'settings.remove_language': 'Remove {name}',
  'settings.translated': '{n} of {total} translated',
  'settings.languages_hint': 'Respondents choose the language on the first page; their choice is saved with the response.',
  'tr.modal_title': 'Translate into {name}',
  'tr.only_missing': 'Only untranslated',
  'tr.all_done': 'Everything is translated.',
  'tr.survey': 'Questionnaire',
  'tr.title': 'Title',
  'tr.description': 'Description',
  'tr.sections': 'Sections',
  'tr.section': 'Section',
  'tr.question': 'Question',
  'save.saving': 'Saving…',
  'save.error': 'Not saved — retrying.',
  'save.retry_now': 'Retry now',
  'save.conflict': 'Conflict',
  'save.saved_ago': 'Saved {ago}',
  'save.all_saved': 'All changes saved',

  // question editor
  'qe.question_n': 'Question {n}',
  'qe.move_up': 'Move up',
  'qe.move_down': 'Move down',
  'qe.duplicate': 'Duplicate question',
  'qe.duplicate_short': 'Duplicate',
  'qe.delete': 'Delete question',
  'qe.delete_short': 'Delete',
  'qe.question': 'Question',
  'qe.question_code': 'Question code (optional)',
  'qe.question_code_hint': 'Letters, numbers, hyphens, and underscores; unique within this questionnaire.',
  'qe.question_code_placeholder': 'e.g. HH-01',
  'qe.section_placeholder': 'e.g. Household information',
  'qe.question_placeholder': 'What would you like to ask?',
  'qe.type': 'Type',
  'qe.change_type': 'Change question type',
  'qe.change_type_hint': 'The title, code, section, info tooltip and logic are kept. Answer options are kept only when switching between choice types.',
  'qe.text_align': "Text alignment",
  'qe.help_text': 'Help text (optional)',
  'qe.help_placeholder': 'Extra guidance shown under the question',
  'qe.tooltip': 'Info tooltip (optional)',
  'qe.tooltip_hint': 'Shown behind an ⓘ icon next to the question title. {used}/{max}',
  'qe.tooltip_placeholder': 'e.g. Definitions, units, or where to find the answer',
  'qe.required': 'Required',
  'qe.options': 'Options',
  'qe.option_n': 'Option {n}',
  'qe.pinned': 'Pinned in place when shuffling',
  'qe.pin': 'Pin in place when shuffling',
  'qe.unpin_option': 'Unpin option',
  'qe.pin_option': 'Pin option',
  'qe.remove_option': 'Remove option {n}',
  'qe.add_option': 'Add option',
  'qe.shuffle_options': 'Shuffle option order',
  'qe.text': 'Text',
  'qe.columns': 'Answer columns',
  'qe.rows': 'Statements (rows)',
  'qe.items': 'Items',
  'qe.fields': 'Fields',
  'qe.column_n': 'Column {n}',
  'qe.row_n': 'Statement {n}',
  'qe.item_n': 'Item {n}',
  'qe.field_n': 'Field {n}',
  'qe.add_column': 'Add column',
  'qe.add_row': 'Add statement',
  'qe.add_item': 'Add item',
  'qe.add_field': 'Add field',
  'qe.add_other': 'Add “Other, please specify”',
  'qe.other_label': 'Other',
  'qe.other_badge': 'asks for text',
  'qe.shuffle_rows': 'Shuffle statements',
  'qe.matrix_multiple': 'Allow several answers per statement',
  'qe.assign_points': 'Assign points',
  'qe.points': 'Points',
  'qe.points_for': 'Points for {label}',
  'qe.agree_1': 'Strongly disagree',
  'qe.agree_2': 'Disagree',
  'qe.agree_3': 'Neutral',
  'qe.agree_4': 'Agree',
  'qe.agree_5': 'Strongly agree',
  'qe.step': 'Step',
  'qe.total': 'Must add up to',
  'qe.total_hint': 'Respondents split this amount between the items, e.g. 100 for percentages.',
  'qe.options_from': 'Options',
  'qe.options_written': 'Written here',
  'qe.carry_selected': 'Options chosen in question {n}',
  'qe.carry_not_selected': 'Options not chosen in question {n}',
  'qe.carry_hint': 'Respondents see the options from that question. If none apply, this question is skipped.',
  'qe.pipe': 'Insert answer',
  'qe.pipe_answers': 'Answer to',
  'qe.pipe_fields': 'Hidden field',
  'qe.pipe_loop': 'Current loop option',
  'qe.pipe_score': 'Score so far',
  'qe.about_file_upload': 'Respondents upload one photo or document (up to 10 MB). Files are private to people who can see responses.',
  'qe.about_signature': 'Respondents draw a signature with a finger, pen or mouse.',
  'qe.about_location': 'Respondents share their GPS location. Their browser or phone asks for permission first.',
  'qe.about_descriptive': 'Shows text and media only; nothing is answered or stored.',
  'library.save': 'Save to question library',
  'library.tab_new': 'New question',
  'library.tab_library': 'Question library',
  'library.tab_copy': 'From another questionnaire',
  'library.search': 'Search saved questions',
  'library.empty': 'The library is empty',
  'library.no_match': 'No saved question matches',
  'library.empty_hint': 'Save a question with the bookmark button in the question editor to reuse it in any questionnaire.',
  'library.saved_by': 'saved by {name}',
  'library.remove': 'Remove from library',
  'library.saved': 'Saved to the question library.',
  'library.pick_survey': 'Choose a questionnaire…',
  'library.copy_hint': "Copies keep their text, options and settings. Display logic and jump rules aren't copied.",
  'library.select_all': 'Select all',
  'library.select_none': 'Clear selection',
  'library.copy_n': 'Copy {count} questions',
  'library.copy_n_one': 'Copy 1 question',
  'qe.scale': 'Scale',
  'qe.scale_min': 'Scale minimum',
  'qe.scale_max': 'Scale maximum',
  'qe.to': 'to',
  'qe.low_label': 'Low label',
  'qe.low_placeholder': 'e.g. Poor',
  'qe.high_label': 'High label',
  'qe.high_placeholder': 'e.g. Excellent',
  'qe.minimum': 'Minimum',
  'qe.maximum': 'Maximum',
  'qe.whole_numbers': 'Whole numbers only',
  'qe.min_length': 'Min length',
  'qe.max_length': 'Max length',
  'qe.placeholder': 'Placeholder',
  'qe.min_selections': 'Min selections',
  'qe.max_selections': 'Max selections',
  'qe.display_logic': 'Display logic',
  'qe.display_logic_sub': 'Skip this question unless earlier answers match',
  'qe.randomization': 'Randomization',
  'qe.randomization_sub': 'Question order is shuffled for each respondent',
  'qe.randomization_sub_off': 'Shuffling is off: questions appear in the order written',
  'qe.keep_position': 'Do not shuffle this question (keep it in its position)',
  'qe.keep_position_on': 'On: this question always stays where it is written, even when the rest is shuffled.',
  'qe.keep_position_off': 'Off: this question is shuffled together with the others in its section.',
  'qe.pinned_by_logic': 'Pinned automatically: questions linked by display logic keep their order so conditions always follow their source.',
  'qe.logic_leads': 'Logic that uses this question',
  'qe.logic_leads_sub': 'Other questions react to the answer given here.',
  'qe.logic_shows': 'When the answer {cond}, shows',
  'qe.logic_carries': 'Carries these options into',
  'qe.logic_go': 'Go to {q}',
  'qe.shuffle_off_hint': 'Shuffling is off for this questionnaire. These options apply once you turn it on in Settings.',
  'qe.randomizer_member': 'This section is in a randomizer: each respondent sees {present} of {total} sections.',
  'qe.edit_in_flow': 'Edit in survey flow',
  'qe.shuffle_with_logic': 'Shuffle this question even though it has logic',
  'qe.shuffle_with_logic_hint': 'It still appears after the question its condition reads, so the logic keeps working.',
  'qe.rich_hint': 'This text has formatting. Editing it here removes the formatting; use Preview mode to keep it.',
  'rich.toolbar': 'Text formatting',
  'rich.align_left': "Align left",
  'rich.align_center': "Centre",
  'rich.align_right': "Align right",
  'rich.align_justify': "Justify",
  'rich.size_down': "Smaller text",
  'rich.size_up': "Bigger text",
  'rich.bold': 'Bold',
  'rich.italic': 'Italic',
  'rich.underline': 'Underline',
  'rich.bullets': 'Bulleted list',
  'rich.numbers': 'Numbered list',
  'rich.link': 'Add link',
  'rich.clear': 'Clear formatting',
  'rich.link_url': 'Link address',
  'rich.apply': 'Apply',
  'rich.link_invalid': 'Use an address that starts with https://, http:// or mailto:',
  'rich.placeholder_title': 'Type the question',
  'rich.placeholder_text': 'Type the instructions',
  'rich.placeholder_desc': 'Add help text',

  // question media
  'media.title': 'Image or video (optional)',
  'media.upload_image': 'Upload image',
  'media.upload_video': 'Upload video',
  'media.uploading': 'Uploading…',
  'media.link': 'Image or video link',
  'media.link_placeholder': 'Paste a YouTube, Vimeo or https:// link',
  'media.add_link': 'Add link',
  'media.hint': 'Images up to {image} MB (JPG, PNG, WebP, GIF). Videos up to {video} MB (MP4, WebM, MOV), or a YouTube/Vimeo link.',
  'media.alt': 'Image description',
  'media.alt_placeholder': 'Describe the image for screen readers',
  'media.caption': 'Caption',
  'media.caption_placeholder': 'Optional text under the video',
  'media.autoplay': 'Play automatically',
  'media.muted': 'Start muted',
  'media.autoplay_hint': 'Most browsers only autoplay muted videos. If sound is blocked, the video starts muted.',
  'media.remove_image': 'Remove image',
  'media.remove_video': 'Remove video',
  'media.add_option_image': 'Add picture',
  'media.add_option_image_n': 'Add a picture to {label}',
  'media.remove_option_image': 'Remove picture',
  'media.remove_option_image_n': 'Remove the picture from {label}',
  'media.too_large_file': "“{name}” is {size} MB. The most a file can be is {max} MB. Shorten or compress the video, or paste a YouTube or Vimeo link instead.",
  'media.uploading_file': "Uploading “{name}”…",
  'error.upload_too_large': "The file is larger than the server accepts. Try a smaller file or a YouTube/Vimeo link.",
  'media.too_large': 'The file is too large (max {max} MB).',
  'media.wrong_kind': 'That file type does not match. Choose an image for images and a video for videos.',
  'media.upload_failed': 'Upload failed. Try again.',
  'media.video': 'Video',
  'media.play': 'Play',
  'media.pause': 'Pause',
  'media.rewind': 'Back 10 s',
  'media.stop': 'Stop',
  'media.mute': 'Mute',
  'media.unmute': 'Unmute',

  // logic editor
  'logic.first_always': 'The first question is always shown. Add logic to later questions to skip them based on earlier answers.',
  'logic.route_sub': "Branching: choose where each answer leads, e.g. “Ayam” → Q3",
  'logic.route_count': "{count} routes",
  'logic.route_title': 'Send each answer to a question',
  'logic.route_hint': 'Pick where the respondent goes after each answer. Answers left on the default continue to the next question.',
  'logic.route_next': 'Next question (default)',
  'logic.route_for': 'Where "{name}" goes',
  'logic.jump_title': 'Jump to a later question (optional)',
  'logic.jump_when': 'When this answer',
  'logic.jump_operator': 'Jump condition',
  'logic.jump_disabled': 'Do not jump',
  'logic.jump_to': 'jump to',
  'logic.jump_target': 'Jump target',
  'logic.always_shown': 'Always shown. Add a condition to show this question only for some respondents.',
  'logic.show_when': 'Show this question when',
  'logic.all': 'all',
  'logic.any': 'any',
  'logic.of_these': 'of these are true:',
  'logic.match': 'Match',
  'logic.question': 'Question',
  'logic.operator': 'Operator',
  'logic.value': 'Value',
  'logic.text_placeholder': 'Text',
  'logic.missing_source': 'Missing or later question',
  'logic.remove': 'Remove condition',
  'logic.add': 'Add condition',
  'logic.row': 'Row or field',
  'logic.whole_question': 'Whole question',
  'logic.carried_hint': '(options carried from an earlier question)',
  'logic.jump_questions': 'Skip to question',
  'logic.jump_endings': 'End the survey',
  'logic.end_with': 'End: {name}',
  'logic.ending_hint': "The respondent submits right away and sees this ending's message.",
  'logic.endings_hint': 'To end the survey early with its own message, add an ending in Settings.',
  'flow.empty': 'Add questions to see the survey flow.',
  'flow.q_ref': 'Q{n}',
  'flow.shown_if': 'Shown if',
  'flow.help': 'Sections are shown in this order. Add a randomizer to show each respondent only some of a group of sections.',
  'flow.show_section': 'Show section:',
  'flow.no_section': '(no section)',
  'flow.n_questions': '({count} questions)',
  'flow.n_questions_one': '({count} question)',
  'flow.toggle_questions': 'Toggle questions',
  'flow.add_below': 'Add below',
  'flow.move_up': 'Move up',
  'flow.move_down': 'Move down',
  'flow.duplicate': 'Duplicate',
  'flow.rename': 'Rename',
  'flow.delete': 'Delete',
  'flow.add_what': "What do you want to add?",
  'flow.add_existing': "Existing section",
  'flow.add_existing_here': "Move it here",
  'flow.add_branch': "Branch",
  'flow.add_end': "End of survey",
  'flow.branch': "Branch:",
  'flow.branch_if': "if {q} {cond}, go to",
  'flow.branch_end': "end of survey ({name})",
  'flow.branch_section': "section “{title}” ({q})",
  'flow.branch_remove': "Remove branch",
  'flow.branch_confirm_remove': "Remove this branch?",
  'flow.branch_title': "Add a branch",
  'flow.branch_help': "Send respondents somewhere else depending on an answer: to a later question, a later section, or the end of the survey. Everyone else continues in order.",
  'flow.end_title': "End the survey here",
  'flow.end_help': "Respondents whose answer matches finish the survey right away and see the ending message.",
  'flow.branch_when': "If the answer to",
  'flow.branch_operator': "Condition",
  'flow.branch_then': "Go to",
  'flow.branch_to_section': "Section",
  'flow.branch_to_question': "Question",
  'flow.branch_to_end': "End of survey",
  'flow.end_new': "End of survey (new ending)",
  'flow.end_default_name': "End of survey",
  'flow.end_default_message': "Thank you, that is the end of this survey.",
  'flow.branch_choice_hint': "Add one branch per answer to send each answer somewhere different.",
  'flow.more_actions': "More actions",
  'flow.ov_title': "Overview",
  'flow.ov_sections': "Sections",
  'flow.ov_questions': "Questions",
  'flow.ov_randomizers': "Randomizers",
  'flow.ov_branches': "Branches",
  'flow.ov_endings': "Endings",
  'flow.ov_how': "How it works",
  'flow.ov_tip_order': "Respondents go from top to bottom. Drag ⋮⋮ to change the order.",
  'flow.ov_tip_add': "Use + to add a section, a branch, a randomizer or an end of survey below an element.",
  'flow.ov_tip_branch': "A branch sends respondents who give a certain answer to a later question, section, or the end.",
  'flow.ov_tip_rz': "A randomizer shows each respondent only some of its sections, in random order.",
  'flow.add_section': 'Section',
  'flow.add_randomizer': 'Randomizer',
  'flow.add_element': 'Add a new element here',
  'flow.randomizer': 'Randomizer',
  'flow.rz_present': 'Randomly present',
  'flow.rz_of': 'of the following {count} sections',
  'flow.rz_fewer': 'Show one section fewer',
  'flow.rz_more': 'Show one section more',
  'flow.rz_empty': 'This randomizer is blank. Add a section below, or drag one in, then choose how many to show.',
  'flow.rz_evenly': 'Evenly present elements',
  'flow.rz_edit_counts': 'Edit counts',
  'flow.rz_counts_title': 'Element counts',
  'flow.rz_counts_help': 'Limit how many respondents are given each element. Leave a limit blank for no limit. With "Evenly present" on, the element given to the fewest respondents so far is shown first.',
  'flow.rz_counts_section': 'Element',
  'flow.rz_counts_given_short': 'given',
  'flow.rz_counts_given': 'Given so far',
  'flow.rz_counts_limit': 'Limit',
  'flow.rz_counts_none': 'No limit',
  'flow.rz_counts_limit_for': 'Limit for {title}',
  'flow.rz_counts_reset': 'Reset all counts',
  'flow.rz_counts_clear': 'Clear limits',
  'flow.rz_counts_failed': 'The counts could not be loaded.',
  'flow.rz_counts_note': "Every respondent is counted when they start the survey, so even presentation and limits work straight away. Publish again after changing limits. Once every element has reached its limit, the ones closest to it are still shown, so respondents are never blocked.",
  'flow.rz_evenly_hint': "Show next the element the fewest respondents have had so far, so every element gets about the same number.",
  'flow.rz_counts_total': "{n} given in total",
  'flow.rz_counts_full': "Full",
  'flow.rz_counts_reset_confirm': "Start every count again from 0? Responses already received are kept.",
  'flow.rz_counts_reset_pending': "The counts start again from 0 once you save and publish.",
  'flow.rz_hint': 'Each respondent sees {n} of these sections, in random order. Keep the sections next to each other.',
  'flow.rz_include_above': 'Include “{title}” above',
  'flow.rz_include_below': 'Include “{title}” below',
  'flow.rz_take_out': 'Take out',
  'flow.rz_remove': 'Remove randomizer',
  'flow.rz_confirm_remove': 'Remove this randomizer? Its sections stay and every respondent sees all of them.',
  'flow.confirm_delete_section': 'Delete the section “{title}” and its {count} question(s)? Logic that uses them is removed too.',
  'flow.name_untitled': 'Name this section',
  'flow.name_taken': 'Another section already has this name.',
  'flow.rename_title': 'Rename section',
  'flow.new_section_title': 'New section',
  'flow.open_new_tab': 'Open in new tab',
  'flow.leads': 'When the answer {cond}, shows',
  'flow.carries_to': 'Carries its options into',
  'flow.shuffled_with_logic': 'Shuffled even though it has logic',
  'all.title': 'All questions',
  'all.device': 'Preview size',
  'all.device_desktop': 'Desktop',
  'all.device_tablet': 'Tablet',
  'all.device_mobile': 'Mobile',
  'all.section_name': 'Section name',
  'all.rz_note': "This view shows every question. Respondents only get the sections their randomizer picks; use the Preview tab to try it.",
  'all.hint': "Click a question to edit it here. Its other options are in the Question tab on the left.",
  'all.section_n': 'Section {n}',
  'all.no_section': 'No section',
  'all.no_section_hint': 'Not in a section yet. Use Add → Group existing questions into a section.',
  'all.editing': 'Editing',
  'all.randomizer': 'Randomizer {present}/{total}',
  'all.edit_question': 'Question {n}: {title}. Select to edit its settings.',
  'flow.and': 'and',
  'flow.or': 'or',
  'flow.jump_if': 'If the answer {cond}, jump to',
  'flow.ends_if': 'Ends the survey if the answer {cond}:',
  'flow.carries_selected': 'Options: those chosen in {q}',
  'flow.carries_not_selected': 'Options: those not chosen in {q}',
  'flow.loops': 'Section repeats for each option chosen in {q}',

  // share panel
  'share.publishing': 'Publishing',
  'share.published_on': 'Version {version} published {date}',
  'share.not_published': 'Not published yet',
  'share.fix_before': 'Fix these before publishing:',
  'share.live_has_changes': 'Respondents still see version {version}. Publish to release your latest edits.',
  'share.closed_note': 'Closed — the link shows a “no longer accepting responses” page.',
  'share.archived_note': 'Archived. Restore it from the questionnaire list to publish again.',
  'share.saving': 'Saving…',
  'share.publish': 'Publish',
  'share.publish_changes': 'Publish changes',
  'share.up_to_date': 'Up to date',
  'share.reopen_publish': 'Reopen & publish',
  'share.close_responses': 'Close responses',
  'share.requires_signin': 'Respondents must sign in',
  'share.anyone': 'Anyone with the link can respond',
  'share.copied': 'Link copied',
  'share.copy_failed': 'Copy failed — select the link manually',
  'share.versions': 'Published versions',
  'share.field_work': 'Field work',
  'share.team': 'Team access',
  'share.team_sub': 'Editors can build and publish; viewers can read results and export.',
  'share.owner': 'Owner',
  'share.remove': 'Remove {email}',
  'share.invite': 'Invite by email',
  'share.invite_placeholder': 'colleague@company.com',
  'share.permission': 'Permission',
  'share.viewer': 'Viewer',
  'share.editor': 'Editor',
  'share.invite_hint': 'The person needs an account. Analysts can only be viewers.',
  'share.granted': 'Access granted',

  // runner
  'runner.preview_unpublished': "You have changes that respondents don’t see yet. Publish the changes to apply them to the survey link.",
  'runner.preview_rz_off': "Randomizer {n} is not applied, so every one of its sections is shown:",
  'runner.preview_rz_picked': "Randomizer {n}: this run shows {sections} ({present} of {total} sections). Restart to get another pick.",
  'runner.preview_rz_will': "Randomizer {n} shows {present} of {total} sections ({sections}) to each respondent. Start to see which one this run gets.",
  'runner.preview_banner': 'Preview — this shows your latest saved changes. Responses are not recorded.',
  'runner.restart': 'Restart',
  'runner.resumed': 'You are continuing the response you started earlier on this device.',
  'runner.start_over': 'Start over',
  'runner.open_tab': 'Open in new tab',
  'runner.progress': 'Question {n} of {total}',
  'runner.page_progress': 'Page {n} of {total}',
  'runner.question_n': 'Question {n}',
  'runner.page_questions': '{count} questions',
  'runner.page_questions_one': '1 question',
  'runner.eyebrow': 'Questionnaire',
  'runner.question_count': "{count} questions",
  'runner.question_count_one': '1 question',
  'runner.start': 'Start',
  'runner.finish': 'Finish',
  'runner.all_done': 'You’re all done',
  'runner.submit_hint': 'Submit to record your answers. You can go back to review them first.',
  'runner.submitting': 'Submitting…',
  'runner.finish_preview': 'Finish preview',
  'runner.submit': 'Submit',
  'runner.preview_complete': 'Preview complete',
  'runner.thank_you': 'Thank you!',
  'runner.nothing_saved': 'Nothing was saved.',
  'runner.recorded': 'Your response has been recorded.',
  'runner.run_again': 'Run again',
  'runner.submit_failed': 'Submission failed.',
  'runner.language': 'Language',
  'runner.screened_title': 'Thank you for your time',
  'runner.screened_body': "We already have enough responses from people like you, so you don't need to continue.",
  'runner.redirecting': 'Taking you to the next page…',
  'runner.would_redirect': 'Respondents will be sent to {url}.',
  'runner.continue': 'Continue',
  'captcha.load_failed': "The human check couldn't load. Check your connection and reload the page.",
  'captcha.failed': "We couldn't verify you're human. Please try submitting again.",
  'runner.time_spent': 'Time spent {time}',
  'runner.about': 'About: {title}',
  'input.type_answer': 'Type your answer',
  'input.select_option': 'Select an option…',
  'input.other_placeholder': 'Please specify',
  'input.slider_unset': 'Drag the slider',
  'input.nps_low': 'Not at all likely',
  'input.nps_high': 'Extremely likely',
  'input.sum_total': 'Total {sum} of {total}',
  'input.sum_left': '{n} left',
  'input.sum_over': '{n} too many',
  'input.file_choose': 'Choose a file',
  'input.file_uploading': 'Uploading…',
  'input.file_replace': 'Replace',
  'input.file_remove': 'Remove file',
  'input.file_hint': 'Photo, PDF, Word, Excel, PowerPoint, CSV or text, up to {mb} MB.',
  'input.file_too_big': 'The file must be {mb} MB or smaller.',
  'input.file_failed': 'Upload failed. Try again.',
  'input.sign_here': 'Sign in the box above',
  'input.signed': 'Signed',
  'input.sign_clear': 'Clear',
  'input.location_share': 'Share my location',
  'input.location_again': 'Update location',
  'input.location_locating': 'Finding your location…',
  'input.location_denied': 'Location access was blocked. Allow it in your browser settings and try again.',
  'input.location_unavailable': "Your location isn't available right now.",
  'input.location_accuracy': 'within {m} m',
  'input.location_view': 'View map',
  'input.location_clear': 'Clear location',
  'tip.more_info': 'More information',

  // public page
  'public.signed_in_as': 'Signed in as {email}',
  'public.requires_signin_title': '“{title}” requires sign-in',
  'public.signin_required': 'Sign in required',
  'public.signin_body': 'This questionnaire is only open to registered respondents.',
  'public.create_account': 'Create account',
  'public.closed_title': 'This questionnaire is closed',
  'public.closed_body': 'It is no longer accepting responses.',
  'public.missing_title': 'Questionnaire not found',
  'public.missing_body': 'Check the link or code {code}.',
  'public.load_failed': 'Could not load the survey.',
  'public.offline_mode': 'Offline: this saved survey is available on this device. New responses will wait here until you reconnect.',
  'public.offline_queued': '{count} response(s) are waiting to sync from this device.',
  'public.offline_failed': 'A queued response was rejected by the server. Review it before retrying or discarding it.',
  'public.sync_now': 'Sync now',
  'public.retry_failed': 'Retry rejected response(s)',
  'public.discard_failed': 'Discard rejected response(s)',

  // responses / dashboard
  'resp.export_csv': 'CSV',
  'resp.export_xlsx': 'Excel',
  'filter.from': 'From',
  'filter.to': 'To',
  'filter.any': 'Any',
  'filter.by_answer': 'Filter by answer',
  'filter.responses_where': 'Responses where',
  'filter.include_test': 'Include {count} test responses',
  'filter.include_test_one': 'Include 1 test response',
  'filter.weighted': 'Weighted',
  'resp.view_summary': 'Summary',
  'resp.view_crosstab': 'Cross-tab',
  'resp.view_list': 'Responses',
  'resp.test': 'Test',
  'resp.no_match': 'No responses match the filter',
  'resp.no_match_hint': 'Clear or loosen the filter to see results.',
  'resp.print': 'Print / PDF',
  'resp.print_hint': 'Print the dashboard or save it as a PDF from the print dialog',
  'resp.export': 'Export',
  'resp.export_blocked': 'Approve every response before exporting',
  'resp.export_sav': 'SPSS (.sav)',
  'resp.export_csv_hint': 'Plain text, opens anywhere',
  'resp.export_xlsx_hint': 'Excel workbook with a question dictionary',
  'resp.export_sav_hint': 'Coded values with labels for SPSS, R or Stata',
  'resp.export_filter_note': 'Exports use the filters and weighting shown above.',
  'resp.weighted_n': 'weighted {n}',
  'resp.score': 'Score',
  'resp.score_sub': 'Average {mean} · from {min} to {max}',
  'resp.endings': 'Endings reached',
  'resp.ending': 'Ending',
  'resp.nps_score': 'Net Promoter Score (−100 to +100)',
  'resp.nps_detractors': 'Detractors 0–6',
  'resp.nps_passives': 'Passives 7–8',
  'resp.nps_promoters': 'Promoters 9–10',
  'resp.avg_rank': 'avg. rank {n}',
  'resp.avg_value': 'avg. {n}',
  'resp.rank_note': 'Lower is better: 1 means ranked first.',
  'resp.sum_note': 'Average amount given to each item.',
  'resp.stat_mean': 'Mean',
  'resp.stat_median': 'Median',
  'resp.stat_min': 'Lowest',
  'resp.stat_max': 'Highest',
  'resp.terms_note': 'Most frequent words, ignoring common words like “the” and “yang”.',
  'resp.no_chart': 'See individual responses for these answers.',
  'resp.looped_note': 'every loop pass counted',
  'resp.other_texts': '“Other” answers',
  'resp.signature_alt': "Respondent's signature",
  'resp.edit': 'Edit answers',
  'resp.edit_title': 'Edit response',
  'resp.edit_note': 'The change is checked against the questionnaire and recorded in the activity log.',
  'resp.edit_saved': 'Response updated.',
  'resp.edited': 'Last edited',
  'xtab.title': 'Cross-tabulation',
  'resp.randomizer_picks': 'Sections shown (randomizer)',
  'xtab.sub': 'Compare answers to two questions',
  'xtab.rows': 'Rows',
  'xtab.columns': 'Columns',
  'xtab.swap': 'Swap rows and columns',
  'xtab.show': 'Show',
  'xtab.mode_count': 'Counts',
  'xtab.mode_row': 'Row %',
  'xtab.mode_col': 'Column %',
  'xtab.mode_total': 'Total %',
  'xtab.total': 'Total',
  'xtab.chi': 'Chi-square {stat}, df {df}, p {p}:',
  'xtab.significant': 'the two are related (significant at 5%).',
  'xtab.not_significant': 'no significant relationship at 5%.',
  'xtab.low_expected': 'Some cells have fewer than 5 expected responses, so treat the test with caution.',
  'xtab.no_chi': 'Not enough data for a chi-square test.',
  'xtab.no_chi_multi': 'No chi-square test: multiple-choice answers fall into several columns at once.',
  'xtab.multi_note': 'Multiple-choice answers are counted once per option chosen, so totals can exceed the number of responses.',
  'xtab.need_two': 'Cross-tabs need two questions to compare',
  'xtab.need_two_hint': 'Add choice, yes/no, rating or NPS questions, or hidden fields, to compare them.',
  'tools.test_title': 'Test responses',
  'tools.test_sub': 'Fill the dashboard with realistic sample data',
  'tools.test_count': 'How many',
  'tools.test_generate': 'Generate',
  'tools.test_created': 'Created {count} test responses.',
  'tools.test_delete': 'Delete {count} test responses',
  'tools.test_delete_one': 'Delete 1 test response',
  'tools.test_confirm_delete': 'Delete all {count} test responses? Real responses are not touched.',
  'tools.test_deleted': 'Deleted {count} test responses.',
  'tools.test_hint': "Test responses follow your logic and validation. They're hidden from the dashboard and exports unless you include them, and never count toward quotas.",
  'tools.test_publish_first': 'Publish the questionnaire to generate test responses.',
  'tools.weight_title': 'Weighting',
  'tools.weight_off': 'Not set up',
  'tools.weight_by': 'Weighted by {name}',
  'tools.weight_setup': 'Set up',
  'tools.weight_edit': 'Change',
  'tools.weight_hint': 'Weighting adjusts results so groups count as much as they do in your population, e.g. 50% women and 50% men. Switch it on in the filter bar.',
  'tools.weight_variable': 'Weight by',
  'tools.weight_variable_hint': 'A question with one answer per respondent, or a hidden field.',
  'tools.weight_sum': 'Targets add up to {sum}%',
  'tools.weight_modal_hint': "Enter each group's share of the population. Groups left at 0 keep their responses unweighted.",
  'tools.weight_remove': 'Remove weighting',
  'tools.weight_saved': 'Weighting saved.',
  'tools.weight_removed': 'Weighting removed.',
  'resp.exporting': 'Exporting…',
  'resp.downloaded': 'Downloaded {name}',
  'resp.export_failed': 'Export failed.',
  'resp.load_failed': 'Could not load the dashboard.',
  'resp.list_failed': 'Could not load responses.',
  'resp.none_yet': 'No responses yet',
  'resp.share_to_collect': 'Share {link} to start collecting.',
  'resp.publish_to_collect': 'Publish the questionnaire to start collecting responses.',
  'resp.all_responses': 'All responses',
  'resp.all_sub': 'Newest first · click a row for details',
  'resp.col_submitted': 'Submitted',
  'resp.col_source': 'Source',
  'resp.col_respondent': 'Respondent',
  'resp.col_version': 'Version',
  'resp.col_answered': 'Answered',
  'resp.col_time': 'Time',
  'resp.showing': 'Showing {from}–{to} of {total}',
  'resp.web': 'Web',
  'resp.mobile': 'Mobile',
  'resp.kpi_total': 'Total responses',
  'resp.kpi_channels': 'Web / mobile',
  'resp.kpi_avg': 'Average time',
  'resp.kpi_median': 'Median time',
  'resp.per_day': 'Responses per day',
  'resp.per_day_sub': 'Last 14 days · busiest {peak}',
  'resp.per_day_aria': 'Responses per day for the last 14 days',
  'resp.day_tip': '{date}: {count}',
  'resp.day_aria': '{date}: {count} responses',
  'resp.channel': 'Collection channel',
  'resp.channel_aria': 'Web {web}, mobile {mobile}',
  'resp.channel_note': 'Mobile responses include those captured offline and synced later.',
  'resp.answered_summary': '{answered} answered · {pct} of responses',
  'resp.mean_summary': ' · mean {mean} (min {min}, max {max})',
  'resp.multi_note': 'Respondents could pick several options; percentages are of those who answered.',
  'resp.numeric_note': 'Numeric answers — see the summary above or export for the full distribution.',
  'resp.text_note': 'Free-text answers are listed per response and in exports.',
  'resp.drawer_title': 'Response',
  'resp.received': 'Received',
  'resp.active_time': 'Active time',
  'resp.submission_id': 'Submission ID',
  'resp.skipped': '— skipped —',

  // users
  'users.title': 'Users & roles',
  'users.add': 'Add user',
  'users.load_failed': 'Failed to load users',
  'users.updated': 'User updated',
  'users.update_failed': 'Update failed',
  'users.accounts': 'Accounts',
  'users.search_placeholder': 'Search name or email',
  'users.search_label': 'Search users',
  'users.none': 'No users found',
  'users.col_user': 'User',
  'users.col_role': 'Role',
  'users.col_status': 'Status',
  'users.col_created': 'Created',
  'users.role_for': 'Role for {email}',
  'users.cant_change_own': 'You can’t change your own role',
  'users.active': 'Active',
  'users.disabled': 'Disabled',
  'users.pending': 'Awaiting approval',
  'users.disable': 'Disable',
  'users.enable': 'Enable',
  'users.created': 'User created',
  'users.creating': 'Creating…',
  'users.create': 'Create user',
  'users.temp_password': 'Temporary password',
  'users.temp_password_hint': 'At least 8 characters. Ask them to change it after signing in.',
  'users.role': 'Role',

  // invitations
  'invitations.title': 'Invitations',
  'invitations.sub': 'Share a sign-up link that gives a chosen role. Each sign-up waits for your approval before it can sign in.',
  'invitations.add': 'New invitation',
  'invitations.create': 'Create link',
  'invitations.creating': 'Creating…',
  'invitations.created': 'Invitation link created and copied',
  'invitations.label': 'Name',
  'invitations.label_placeholder': 'e.g. Jakarta field team',
  'invitations.label_hint': 'Only you see this. It helps tell links apart.',
  'invitations.role_hint': 'Everyone who signs up with this link gets this role.',
  'invitations.max_uses': 'Usage limit',
  'invitations.max_uses_hint': 'How many sign-ups the link accepts. Leave empty for no limit.',
  'invitations.col_label': 'Invitation',
  'invitations.col_role': 'Role',
  'invitations.col_uses': 'Used',
  'invitations.col_pending': 'Waiting',
  'invitations.col_status': 'Status',
  'invitations.unlimited': 'no limit',
  'invitations.untitled': 'Untitled link',
  'invitations.status_active': 'Active',
  'invitations.status_revoked': 'Revoked',
  'invitations.status_full': 'Limit reached',
  'invitations.copy_link': 'Copy link',
  'invitations.link_copied': 'Link copied',
  'invitations.copy_failed': 'Couldn’t copy. Select the link and copy it by hand: {url}',
  'invitations.review': 'Review sign-ups',
  'invitations.revoke': 'Revoke',
  'invitations.reactivate': 'Reactivate',
  'invitations.confirm_revoke': 'Revoke this link? Nobody can sign up with it any more. People already waiting can still be approved.',
  'invitations.updated': 'Invitation updated',
  'invitations.update_failed': 'Couldn’t update the invitation',
  'invitations.load_failed': 'Failed to load invitations',
  'invitations.none': 'No invitation links yet',
  'invitations.none_hint': 'Create a link, choose the role and a usage limit, then share it.',
  'invitations.signups_title': 'Sign-ups: {label}',
  'invitations.signups_none': 'Nobody has signed up with this link yet.',
  'invitations.select_all': 'Select all waiting',
  'invitations.approve_selected': 'Approve selected',
  'invitations.reject_selected': 'Reject selected',
  'invitations.approve_all': 'Approve all ({n})',
  'invitations.approved_n': '{n} sign-up(s) approved',
  'invitations.rejected_n': '{n} sign-up(s) rejected',
  'invitations.confirm_reject': 'Reject {n} sign-up(s)? Their accounts are deleted and they can be invited again.',
  'invitations.signup_waiting': 'Waiting',
  'invitations.signup_approved': 'Approved',

  // account
  'account.title': 'Account',
  'account.respondent_note':
    'You’re signed in as a respondent. Open a survey link you received to answer it, or use the mobile app for field surveys. An administrator can give you access to build questionnaires or view results.',
  'account.profile': 'Profile',
  'account.display_name': 'Display name',
  'account.updated': 'Profile updated',
  'account.profile_hint': 'This is how you appear to your team.',
  'account.job_title': 'Job title',
  'account.job_title_placeholder': 'For example, Research lead',
  'account.bio': 'About you',
  'account.bio_placeholder': 'A line or two about what you work on.',
  'account.banner': 'Banner',
  'account.banner.meadow': 'Meadow',
  'account.banner.ocean': 'Ocean',
  'account.banner.sunset': 'Sunset',
  'account.banner.dusk': 'Dusk',
  'account.banner.rose': 'Rose',
  'account.banner.slate': 'Slate',
  'account.banner.aurora': 'Aurora',
  'account.banner.sand': 'Sand',
  'account.photo': 'Profile photo',
  'account.photo_hint': 'JPEG, PNG or WebP. You can zoom and move it to fit.',
  'account.photo_upload': 'Upload photo',
  'account.photo_change': 'Change photo',
  'account.photo_remove': 'Remove photo',
  'account.photo_updated': 'Photo updated',
  'account.photo_removed': 'Photo removed',
  'account.photo_failed': 'Couldn’t update the photo',
  'account.crop_title': 'Adjust your photo',
  'account.crop_zoom': 'Zoom',
  'account.crop_use': 'Use photo',
  'account.crop_hint': 'Drag to move the photo. With the picture focused, use the arrow keys.',
  'account.crop_unreadable': 'This image can’t be read. Try a JPEG, PNG or WebP.',
  'account.member_since': 'Member since {date}',
  'account.unsaved': 'Unsaved changes',
  'account.reset': 'Reset',
  'account.access': 'Your access',
  'account.access_hint': 'What your role lets you do.',
  'account.access_more': '+{count} more',
  'account.update_failed': 'Update failed',
  'account.display': 'Display',
  'account.display_hint': 'These settings apply to this browser only.',
  'account.accent_save': 'Save colours',
  'account.accent': "Accent colour",
  'account.accent_light': "Light mode",
  'account.accent_dark': "Dark mode",
  'account.accent_default': "Default",
  'account.accent_custom': "Custom colour",
  'account.accent_custom_light': "Custom colour for light mode",
  'account.accent_custom_dark': "Custom colour for dark mode",
  'account.accent_hint': "Used for highlights, selected items and progress. It is saved on your account, so it follows you to every device.",
  'account.accent_saved': "Accent colours saved",
  'account.zoom': 'Zoom layout',
  'account.zoom_hint': 'Makes the whole app bigger or smaller. 100% is the default.',
  'account.sidebar': 'Sidebar',
  'account.sidebar_expanded': 'Expanded',
  'account.sidebar_collapsed': 'Collapsed (icons only)',
  'account.sidebar_hint': 'You can also collapse it with the button at the top of the sidebar. On small screens it always opens as a drawer.',
  'account.password': 'Password',
  'account.current_password': 'Current password',
  'account.new_password': 'New password',
  'account.change_password': 'Change password',
  'account.password_changed': 'Password changed',
  'account.language': 'Language',
  'account.language_hint': 'Applies to this browser. Questionnaire content is shown as written.',
  'respondents.title': 'Respondent roster',
  'respondents.subtitle': 'Named participants for field collection. Gender can be corrected by an administrator or creator.',
  'respondents.add': 'Add respondent',
  'respondents.search': 'Search name or participant ID',
  'respondents.name': 'Name',
  'respondents.external_id': 'Participant ID (optional)',
  'respondents.gender': 'Gender',
  'respondents.active': 'Active',
  'respondents.inactive': 'Inactive',
  'respondents.female': 'Female',
  'respondents.male': 'Male',
  'respondents.nonbinary': 'Non-binary',
  'respondents.self_described': 'Self-described',
  'respondents.prefer_not_to_say': 'Prefer not to say',
  'respondents.unspecified': 'Unspecified',
  'respondents.none': 'No respondents yet',
  'respondents.create': 'Create record',
  'respondents.save': 'Save changes',
  'respondents.created': 'Respondent added',
  'respondents.updated': 'Respondent updated',
  'respondents.load_failed': 'Could not load respondents.',
  'respondents.assignments_title': 'Respondent assignments',
  'respondents.assignments_help': 'Assign each named respondent to one enumerator for this survey.',
  'respondents.assignments_saved': 'Respondent assignments saved',
  'groups.title': 'Group randomization',
  'groups.help': 'Randomly assign survey respondents to groups using exact quotas within each gender stratum.',
  'groups.stratify_by': 'Balance assignments by',
  'groups.gender': 'Gender',
  'groups.none': 'No strata',
  'groups.group': 'Group',
  'groups.stratum': 'Stratum',
  'groups.showing_first': 'Showing the first {count} of {total} assignments.',
  'groups.all_respondents': 'All respondents',
  'groups.total': 'Quota total',
  'groups.add': 'Add group',
  'groups.randomize': 'Randomize and save assignments',
  'groups.randomized': 'Group assignments randomized and saved',
  'groups.assign_respondents_first': 'Assign respondents to enumerators before randomizing groups.',
  'groups.lock_hint': 'Assignments are stored with the survey and included in the offline mobile catalogue. They cannot be randomized again after the first response is collected.',
  'groups.load_failed': 'Could not load group randomization data.',
  'respondents.unassigned': 'Unassigned',
  'respondents.assigned_count': '{count} of {total} assigned',
  'approval.approve': 'Approve',
  'approval.reject': 'Reject',
  'approval.status': 'Approval',
  'approval.pending': 'Pending',
  'approval.approved': 'Approved',
  'approval.rejected': 'Rejected',
  'approval.survey_pending': 'This revision is pending admin approval. It cannot be published until approved.',
  'approval.survey_approved': 'This revision is approved for publishing.',
  'approval.survey_rejected': 'This revision was rejected. Edit it so an admin can review the new revision.',
  'approval.response_approved': 'Response approved',
  'approval.response_rejected': 'Response rejected',
  'approval.export_blocked': '{count} responses still need approval. Exports are blocked until all responses are approved.',
  'respondents.invalid_name': 'Enter a name of up to 200 characters.',
  'respondents.invalid_gender': 'Choose a valid gender value.',
  'enumerators.title': 'Enumerator assignments',
  'enumerators.help': 'Only assigned enumerators can download this survey in the mobile app and sync its responses.',
  'enumerators.none': 'No active enumerators are available. Add an enumerator account first.',
  'enumerators.save': 'Save assignments',
  'enumerators.saved': 'Enumerator assignments saved',
  'enumerators.load_failed': 'Could not load enumerator assignments.',
  'enumerators.selected': '{count} of {total} selected',
  'enumerators.too_many': 'Assign no more than 500 enumerators to a survey.',
  'enumerators.invalid_list': 'The enumerator list contains an invalid or repeated user.',
  'enumerators.user_unavailable': 'One or more selected accounts are inactive or no longer enumerators.',
  'enumerators.survey_not_assigned': 'This survey is not assigned to your enumerator account.',

  // AI assistant
  'assistant.open': 'Ask the assistant',
  'assistant.title': 'Assistant',
  'assistant.new_chat': 'New chat',
  'assistant.placeholder': 'Ask about your questionnaires or results…',
  'assistant.send': 'Send',
  'assistant.stop': 'Stop',
  'assistant.intro': 'I can look up your questionnaires and results, and draft questions for you to review. I only see what you can see, and I never save anything without your OK.',
  'assistant.suggest_results': 'Summarize the results of this questionnaire',
  'assistant.suggest_improve': 'How could this questionnaire be improved?',
  'assistant.suggest_text': 'What themes come up in the written answers?',
  'assistant.suggest_list': 'Which questionnaires have the most responses?',
  'assistant.suggest_new': 'Draft a short customer satisfaction survey',
  'assistant.working': 'Thinking…',
  'assistant.tool.list_surveys': 'Looking up questionnaires…',
  'assistant.tool.get_survey': 'Reading the questionnaire…',
  'assistant.tool.get_results': 'Reading results…',
  'assistant.tool.list_variables': 'Checking what can be compared…',
  'assistant.tool.crosstab': 'Running a cross-tab…',
  'assistant.tool.response_trend': 'Checking responses over time…',
  'assistant.tool.describe': 'Calculating statistics…',
  'assistant.tool.compare_groups': 'Comparing groups…',
  'assistant.tool.drivers': 'Looking for what goes with the outcome…',
  'assistant.tool.data_quality': 'Checking response quality…',
  'assistant.tool.field_progress': 'Checking field progress…',
  'assistant.tool.compare_surveys': 'Comparing questionnaires…',
  'assistant.suggest_trend': 'How are responses trending over time?',
  'assistant.suggest_quality': 'Check the quality of the responses',
  'assistant.sources': 'Sources ({count})',
  'assistant.source_responses': '{count} responses',
  'assistant.source_responses_one': '{count} response',
  'assistant.chart.data': 'Show data',
  'assistant.chart.category': 'Category',
  'assistant.tool.get_text_answers': 'Reading written answers…',
  'assistant.tool.propose_question_changes': 'Drafting changes…',
  'assistant.tool.propose_new_survey': 'Drafting a questionnaire…',
  'assistant.proposal_edit': 'Proposed changes to “{title}”',
  'assistant.proposal_create': 'New questionnaire: “{title}”',
  'assistant.proposal_counts': '{added} added · {updated} changed · {removed} removed',
  'assistant.proposal_questions': '{count} questions',
  'assistant.proposal_questions_one': '{count} question',
  'assistant.preview': 'Show questions',
  'assistant.apply': 'Apply',
  'assistant.dismiss': 'Dismiss',
  'assistant.applied': 'Applied',
  'assistant.dismissed': 'Dismissed',
  'assistant.apply_conflict': 'The questionnaire changed after this was drafted. Ask again for a fresh proposal.',
  'assistant.created': 'Questionnaire created.',
  'assistant.updated': 'Changes applied.',
  'assistant.failed': 'The assistant could not answer. Please try again.',
  'nav.assistant': 'Assistant',
  'assistant.open_full': 'Open full page',
  'assistant.page_heading': 'What would you like to know?',
  'assistant.chats': 'Recent chats',
  'assistant.no_chats': 'Your chats appear here. They are kept in this browser only.',
  'assistant.delete_chat': 'Delete chat',
  'assistant.turned_off': 'An administrator turned the AI assistant off for this workspace.',
  'settings.title': 'Settings',
  'settings.sub': 'Workspace-wide options. Changes apply to every user right away.',
  'settings.saved': 'Settings saved',
  'settings.assistant': 'AI assistant',
  'settings.assistant_enabled': 'Turn on the AI assistant and chat',
  'settings.assistant_hint': 'When off, the Assistant menu and chat panel are hidden for everyone and the server rejects chat requests.',
  'settings.assistant_demo': 'No AI model is connected: the server runs the rule-based demo. Set ASSISTANT_* on the server to connect a model.',
  'settings.assistant_not_configured': 'No AI model is configured on this server, so this switch has no effect until ASSISTANT_* is set.',
  'assistant.not_enabled': 'The AI assistant is not set up on this server. An administrator can turn it on with the ASSISTANT_* settings.',
  'assistant.suggest_compare': 'Compare answers between groups in my latest questionnaire',
  'assistant.setup_intro': 'To turn it on, add these settings to the API environment (api/.env locally, /etc/survey/env or the GitHub deploy settings in production):',
  'assistant.setup_after': 'Then restart the API (./run.sh locally) and reload this page.',
  'assistant.demo_banner': 'Demo mode: no AI model is connected yet. Answers follow simple rules but use your real data. Set ASSISTANT_* on the server to connect a model.',
  'assistant.disclaimer': 'AI can make mistakes. Check important numbers in the dashboard.',
  'tabs.invites': 'Invitations',
  'public.invite_hello': 'Hi {name}, thanks for taking part.',
  'public.closes_on': 'Open until {date}.',
  'public.not_open_title': 'This questionnaire isn’t open yet',
  'public.not_open_body': 'It opens on {date}. Come back then.',
  'public.not_open_soon': 'It opens soon. Come back later.',
  'public.invite_required_title': 'By invitation only',
  'public.invite_required_body': 'Open this questionnaire with the personal link from your invitation email.',
  'public.invite_invalid_title': 'Invitation link not recognised',
  'public.invite_invalid_body': 'Check that you used the full link from your invitation email, or ask the organiser for a new one.',
  'public.invite_used_title': 'Already answered',
  'public.invite_used_body': 'This invitation was already used to answer the questionnaire. Thank you!',
  'progress.kpi_responses': 'Responses',
  'progress.kpi_today': 'Today',
  'progress.kpi_active': 'Enumerators active today',
  'progress.kpi_invites': 'Invitations answered',
  'progress.kpi_channels': 'Web / mobile',
  'progress.per_day': 'Responses per day',
  'progress.per_day_sub': 'Last {days} days, all channels',
  'progress.quotas': 'Quotas',
  'progress.quotas_close': 'The survey closes when every quota is full.',
  'progress.invites': 'Invitations',
  'progress.invites_sent': 'Emailed',
  'progress.invites_opened': 'Opened',
  'progress.invites_completed': 'Answered',
  'progress.auto_closed_schedule': 'Closed automatically at its closing time ({date}).',
  'progress.auto_closed_max_responses': 'Closed automatically on reaching its response limit ({date}).',
  'progress.auto_closed_quotas_full': 'Closed automatically when every quota filled ({date}).',
  'progress.opens_on': 'Opens {date}',
  'progress.closes_on': 'Closes {date}',
  'progress.no_enumerators': 'No enumerators yet',
  'progress.no_enumerators_body': 'Assign enumerators under Publish & share to follow their field work here.',
  'progress.enumerators': 'Enumerators',
  'progress.enumerators_sub': 'Responses per day over the last {days} days, respondents completed and the last upload from each device',
  'progress.col_enumerator': 'Enumerator',
  'progress.col_total': 'Total',
  'progress.col_respondents': 'Respondents done',
  'progress.col_review': 'Approved · rejected',
  'progress.col_last_upload': 'Last upload',
  'progress.approved': 'Approved',
  'progress.rejected': 'Rejected',
  'progress.never': 'Never',
  'invites.reminders_queued': '{count} reminders are being sent.',
  'invites.reminders_queued_one': '1 reminder is being sent.',
  'invites.emails_queued': '{count} invitations are being sent.',
  'invites.emails_queued_one': '1 invitation is being sent.',
  'invites.nothing_to_send': 'Nobody to email: everyone selected has been emailed or has answered.',
  'invites.remove_confirm': 'Remove {count} invitations? Their links will stop working.',
  'invites.remove_confirm_one': 'Remove this invitation? Its link will stop working.',
  'invites.removed': '{count} invitations removed.',
  'invites.removed_one': '1 invitation removed.',
  'invites.download_csv': 'Download links (CSV)',
  'invites.add': 'Add respondents',
  'invites.email_off': 'Email isn’t set up on this server, so invitations can’t be emailed yet. You can still add people and share their personal links (copy one, or download them all as CSV).',
  'invites.publish_first': 'Publish the questionnaire before emailing invitations.',
  'invites.mode_invite_only': 'Invitation only: web respondents need their personal link to answer.',
  'invites.mode_open': 'Anyone with the public link can answer. Personal links additionally track who answered and allow one response each.',
  'invites.change_mode': 'Change',
  'invites.empty_title': 'No invitations yet',
  'invites.empty_body': 'Add respondents by email. Each gets a personal link that works once, and you can send reminders to those who haven’t answered.',
  'invites.status_total': 'All',
  'invites.status_new': 'Not emailed',
  'invites.status_queued': 'Sending',
  'invites.status_sent': 'Emailed',
  'invites.status_opened': 'Opened',
  'invites.status_completed': 'Answered',
  'invites.status_failed': 'Failed',
  'invites.selected': '{count} selected',
  'invites.send': 'Send invitation',
  'invites.remind': 'Send reminder',
  'invites.remove': 'Remove',
  'invites.send_all_new': 'Email everyone not emailed ({count})',
  'invites.remind_all': 'Remind everyone who hasn’t answered',
  'invites.select_all': 'Select all',
  'invites.col_person': 'Respondent',
  'invites.col_status': 'Status',
  'invites.col_emailed': 'Emailed',
  'invites.col_answered': 'Answered',
  'invites.reminded': '{count} reminders',
  'invites.reminded_one': '1 reminder',
  'invites.opened_at': 'Opened {when}',
  'invites.copy_link': 'Copy personal link',
  'invites.added': '{added} added, {skipped} already invited.',
  'invites.invalid_emails': 'Not valid email addresses: {list}',
  'invites.add_title': 'Add respondents',
  'invites.add_count': 'Add {count}',
  'invites.add_count_one': 'Add 1',
  'invites.paste_label': 'Email addresses',
  'invites.paste_hint': 'One per line, with an optional name: “ana@example.com, Ana” or “Ana <ana@example.com>”. Paste straight from a spreadsheet.',
  'invites.send_now': 'Email the invitation now',
  'invites.send_now_unavailable': 'Emailing isn’t available (email not set up, or the questionnaire isn’t published). Links can be shared by hand.',
  'collection.saved': 'Schedule saved',
  'collection.title': 'Schedule & limits',
  'collection.help': 'When responses are accepted, when the survey closes itself, and who may answer.',
  'collection.opens_at': 'Opens at',
  'collection.opens_hint': 'Empty: open as soon as it’s published.',
  'collection.closes_at': 'Closes at',
  'collection.closes_hint': 'Empty: stays open until closed by hand.',
  'collection.max_responses': 'Close after this many responses',
  'collection.max_responses_hint': 'Test responses don’t count.',
  'collection.no_limit': 'No limit',
  'collection.close_on_quotas': 'Close when every quota is full',
  'collection.close_on_quotas_none': 'Close when every quota is full (add quotas in Settings first)',
  'collection.invite_only': 'Invitation only (web respondents need a personal link)',
  'collection.reminder_days': 'Remind unanswered invitations every (days)',
  'collection.reminder_hint': 'Empty: no automatic reminders.',
  'collection.reminder_no_email': 'Needs email to be set up on the server.',
  'collection.no_reminders': 'Off',
  'collection.max_reminders': 'At most (reminders)',
  'collection.unsaved': 'Unsaved changes',
  'collection.save': 'Save schedule',
  'invitations.expired_on': 'Expired {date}',
  'invitations.expires_on': 'Expires {date}',
  'invitations.joins': 'Joins: {list}',
  'invitations.status_expired': 'Expired',
  'invitations.edit': 'Edit',
  'invitations.edit_title': 'Edit invitation',
  'invitations.save': 'Save',
  'invitations.expires_at': 'Expires at',
  'invitations.expires_hint': 'The link stops working at this time. Leave empty for no expiry.',
  'invitations.surveys': 'Add to questionnaires on approval',
  'invitations.surveys_hint_enumerator': 'Approved enumerators are assigned to these questionnaires straight away.',
  'invitations.surveys_hint_other': 'Approved people get access to these questionnaires as viewer or editor.',
  'invitations.surveys_none': 'No questionnaires yet.',
  'invitations.access_viewer': 'Viewer',
  'invitations.access_editor': 'Editor',
  'tabs.field': 'Field work',
  'field.nav_progress': 'Progress',
  'field.nav_assignments': 'Assignments',
  'field.search_enumerators': 'Search enumerators',
  'field.search_respondents': 'Search name or ID',
  'field.no_match': 'Nothing matches.',
  'field.unsaved': 'unsaved changes',
  'field.filter_all': 'All',
  'field.filter_unassigned': 'Unassigned',
  'field.filter_assigned': 'Assigned',
  'field.filter_enumerator': 'Filter by enumerator',
  'field.any_enumerator': 'Any enumerator',
  'field.picked_one': '1 selected',
  'field.picked': '{count} selected',
  'field.assign_to': 'Assign to',
  'field.assign_picked': 'Assign',
  'field.unassign_picked': 'Unassign',
  'field.clear_pick': 'Clear',
  'field.pick_page': 'Select all on this page',
  'field.external_id': 'ID',
  'field.enumerator': 'Enumerator',
  'field.enumerator_for': 'Enumerator for {name}',
  'field.unknown_enumerator': 'Unavailable enumerator',
  'field.changed_one': '1 unsaved change',
  'field.changed': '{count} unsaved changes',
  'field.prev_page': 'Previous page',
  'field.next_page': 'Next page',
  'field.page_of': '{page} / {pages}',
  'groups.summary_one': '{groups} groups · 1 respondent',
  'groups.summary': '{groups} groups · {count} respondents',
  'groups.not_yet': 'Not randomized yet',
  'builder.general': 'General',
  'builder.messages': 'Messages',
  'settings.endings_count_one': '1 extra ending',
  'settings.endings_count': '{count} extra endings',
  'settings.endings_none_short': 'Everyone sees the thank-you message',
  'settings.quotas_none_short': 'No quotas',
  'settings.quota_no_conditions': 'No conditions yet',
  'settings.quota_or': 'or',
  'settings.none_short': 'None',
  'settings.loops_count_one': '1 looped section',
  'settings.loops_count': '{count} looped sections',
  'settings.theme_custom': 'Custom look',
  'settings.theme_default_short': 'Default colors and font',
  'collection.sum_opens': 'Opens {date}',
  'collection.sum_opened': 'Opened {date}',
  'collection.sum_closes': 'Closes {date}',
  'collection.sum_no_close': 'No closing date',
  'collection.sum_max': 'Max {n} responses',
  'collection.sum_quotas': 'Closes when quotas are full',
  'collection.sum_invite_only': 'Invite only',
  'collection.sum_reminders': 'Reminder every {n} days',
  'collection.auto_closed_badge': 'Auto-closed',
  'share.code_short': 'Code {code}',
  'share.field_work_moved': 'Enumerators, respondent assignments and groups now live in the Field work tab.',
  'share.link_title': 'Link & QR code',
  'share.link_sub': 'Give the link a name people can remember, or let them scan a code.',
  'share.custom_link': 'Custom link',
  'share.slug_placeholder': 'customer-survey-2026',
  'share.slug_hint': 'Leave empty to use the random code.',
  'share.slug_invalid': 'Use {min} to {max} lowercase letters or numbers, with single hyphens between them.',
  'share.slug_saved': 'Custom link saved',
  'share.slug_removed': 'Custom link removed',
  'share.slug_remove': 'Remove custom link',
  'share.code_still_works': 'The original link {link} keeps working.',
  'share.qr_title': 'QR code',
  'share.qr_alt': 'QR code that opens {link}',
  'share.qr_hint': 'Scan it to open the questionnaire. Download it for posters, flyers or screens.',
  'share.qr_logo': 'Put the YellowMetrics logo in the middle',
  'share.copy_link': 'Copy link',
  'share.versions_sub_one': 'v{version} · {date}',
  'share.versions_sub': '{count} versions · latest v{version} · {date}',
  'builder.settings_search': 'Search settings, e.g. captcha, quota, language',
  'builder.settings_found_one': '1 section matches',
  'builder.settings_found': '{count} sections match',
  'builder.settings_none': 'No settings match your search',
  'tabs.activity': 'Activity',
  'resp.answers_note': 'Answers as typed, most frequent first.',
  'resp.answers_more': 'Showing the {shown} most frequent of {total} different answers. Export to see them all.',
};

export type MessageKey = keyof typeof en;
